// External tools
import Vue, { computed } from 'vue'
import AsyncComputed from 'vue-async-computed'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createPinia, PiniaVuePlugin } from 'pinia'
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons'
import { faCog, faTrashAlt, faPlusCircle, faClipboard, faAngleDown } from '@fortawesome/pro-light-svg-icons'
import {
  faQuestionCircle,
  faUser,
  faFileCsv,
  faFilePdf,
  faFileCode,
  faFileLines,
  faArrowsRotate,
} from '@fortawesome/pro-solid-svg-icons'

// Internal tools
import App from '@/App.vue'
import router from '@/router'
import i18n from '@/i18n/SetupI18n.js'
import { setupIntegrations } from '@/third-party-integrations/integrations.js'
import { buildAppConfiguration } from '@/helpers.js'
import { useAuthStore } from '@/stores/auth.js'
import { getHttpClient, initialiseHttpClient } from '@/utilities/httpClients'
import vueQueryPluginOptions from '@/utilities/vueQueryPluginOptions'
import runtimeProviders from '@/providers/runtime.js'
import { API_GATEWAY_CLIENT, CPQ_LITE_CLIENT, X_APP } from '@/Globals.js'
import '@/plugins/element.js'
import '@/demoData.js'

const timeoutPathWhitelist = ['/login', '/mfa', '/post-login']

// Pinia Instance
const pinia = createPinia()

// Add Plugins
Vue.use(AsyncComputed)
Vue.use(VueQueryPlugin, vueQueryPluginOptions)
Vue.use(PiniaVuePlugin)
Vue.use(pinia)

// Add font awesome icons to the library
library.add(
  // Regular
  faEye,
  faEyeSlash,
  // Light
  faCog,
  faTrashAlt,
  faPlusCircle,
  faClipboard,
  faAngleDown,
  // Solid
  faQuestionCircle,
  faUser,
  faFileCsv,
  faFilePdf,
  faFileCode,
  faFileLines,
  faArrowsRotate,
)

// Register the font awesome icon component
Vue.component('FontAwesomeIcon', FontAwesomeIcon)

if (process.env.NODE_ENV === 'development') {
  Vue.config.devtools = true
  Vue.config.performance = true
  Vue.config.productionTip = false
}

// Dynamic import environment file
import(/* @vite-ignore */ `${import.meta.env.BASE_URL}environment.js`).then(async environment => {
  // Environment
  const appConfiguration = buildAppConfiguration(environment.default)
  Vue.prototype.$appConfiguration = appConfiguration

  const authStore = useAuthStore()

  // CPQLite API
  initialiseHttpClient(CPQ_LITE_CLIENT, {
    baseUrl: appConfiguration.cpqApi,
    token: computed(() => authStore.cpqLiteJwt),
  })
  // Megalith API
  initialiseHttpClient(API_GATEWAY_CLIENT, {
    baseUrl: appConfiguration.publicApi,
    token: computed(() => authStore.jwt ?? localStorage.getItem('_accessToken')),
    xApp: X_APP,
  })

  // Integrations
  setupIntegrations(appConfiguration)

  // Init app
  const settings = {
    router,
    render: h => h(App),
    i18n,
    provide: runtimeProviders,
    pinia,
  }

  if (import.meta.env.VITE_USE_MOCK_API === 'true') {
    const { getWorker } = await import('./mocks/browser')
    const { onUnhandledRequest } = await import('./mocks/utils')

    const worker = getWorker(appConfiguration)

    worker.start({ onUnhandledRequest: onUnhandledRequest(appConfiguration) })

    // NOTE: Uncomment the following event listener to see all intercepted requests in the console
    // worker.events.on('request:start', ({ request }) => {
    //   console.warn('Outgoing:', request.method, request.url)
    // })
  }

  window.mpApp = new Vue(settings).$mount('#mp')

  getHttpClient(API_GATEWAY_CLIENT).interceptors.response.use(null, error => {
    // Don't auto log out on some paths, let them handle it themselves.
    if (error?.status === 401 && !timeoutPathWhitelist.includes(window.mpApp.$route.path)) {
      authStore.handleSessionTimeout()
    }
    throw error
  })
})
